import React, { useState, useEffect } from "react";
import { useWidgetComponent } from "./public/registry";
import { filterProofs, findManifest } from "./public/manifest";
import { widgetPalette, widgetCss } from "./public/shared/colorUtils";

export const Preview = ({ widget, fetchMethod, selectedProofs, filterLabelIds = [], requireAvatar = false, previewMode = "desktop" }) => {
  const { kind } = widget;
  const manifest = findManifest(kind);
  // The editor switches kinds constantly, so unlike the embed this loading
  // state is genuinely visible and gets its own message below.
  const { Component, state: componentState } = useWidgetComponent(kind);
  const limit = manifest?.maxProofs || 50;

  const [proofs, setProofs] = useState(selectedProofs || []);
  // Stable string key so useEffect re-fires when chip set changes (an
  // array reference would be a new object every render even when the
  // contents are identical).
  const filterKey = filterLabelIds.slice().sort((a, b) => a - b).join(",");

  const fetchProofs = async () => {
    let url = `/api/v1/widgets/${widget.uuid}/available_proofs?limit=${limit}`;
    // Ask only for kinds this widget renders, matching WidgetShow. Without
    // it a widget like Hero Video (limit 1, video only) fetches the newest
    // proof, filters it out client-side, and shows nothing.
    if (manifest?.kinds?.length) url += `&kinds=${manifest.kinds.join(",")}`;
    if (filterKey) url += `&label_ids=${filterKey}`;
    if (requireAvatar) url += "&require_avatar=1";
    const response = await fetch(url);
    if (!response.ok) return;
    const data = await response.json();
    setProofs(data.proofs || []);
  };

  useEffect(() => {
    if (fetchMethod === "Automatic") {
      fetchProofs();
      return;
    }
    setProofs(selectedProofs || []);
  }, [selectedProofs, fetchMethod, kind, limit, filterKey, requireAvatar, manifest]);

  // Filter by manifest kinds BEFORE slicing to maxProofs — otherwise a
  // single-proof widget like HeroQuote (limit=1, text-only) takes the
  // latest proof, then drops it if it isn't text, and renders blank.
  const compatible = manifest ? filterProofs(manifest, proofs) : proofs;
  const compatibleProofs = manifest ? compatible.slice(0, limit) : compatible;

  const maxWidth = previewMode === "mobile" ? "390px" : "100%";
  const scopedCss = widgetCss(widgetPalette(widget));

  return (
    <div
      className="mx-auto transition-[max-width] duration-300 ease-out"
      style={{ maxWidth }}
    >
      <div className="border-[3px] border-black bg-white shadow-[6px_6px_0_rgba(0,0,0,0.18)] overflow-hidden md:h-[calc(100vh-160px)] flex flex-col">
        <div className="flex-none flex items-center gap-1.5 px-3 py-2 border-b-[2px] border-black bg-slate-100">
          <span className="block w-2.5 h-2.5 rounded-full bg-[#FF7AAC] border border-black/40" />
          <span className="block w-2.5 h-2.5 rounded-full bg-[#FFD23A] border border-black/40" />
          <span className="block w-2.5 h-2.5 rounded-full bg-[#3EB567] border border-black/40" />
          <div className="ml-3 flex-1 h-5 bg-white border border-black/20 rounded-sm flex items-center px-2">
            <span className="text-[10px] font-bold text-slate-400 truncate">your-site.com</span>
          </div>
        </div>
        <div className="ss-widget-scope relative bg-gray-50 overflow-hidden flex-1 min-h-[400px]">
          {/* The preview renders Component directly rather than through
              WidgetShow, so it needs the same scoped rules or the editor
              disagrees with the live embed. */}
          {scopedCss && <style>{scopedCss}</style>}
          {Component ? (
            compatibleProofs.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-center text-slate-700 px-6">
                <div>
                  <p className="font-black text-xl">No compatible proofs to preview</p>
                  {manifest?.kinds?.length > 0 && (
                    <p className="text-sm mt-2 opacity-70">
                      This widget shows {manifest.kinds.join(" / ")} proofs.
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <Component widget={widget} initProofs={compatibleProofs} />
            )
          ) : componentState === "loading" ? (
            <div className="m-auto text-center text-slate-400 absolute inset-0 flex items-center justify-center">
              <p className="text-sm font-bold">Loading {kind}…</p>
            </div>
          ) : (
            <div className="m-auto text-center text-slate-700 absolute inset-0 flex items-center justify-center">
              <div>
                <p className="font-black text-xl">Unknown widget kind</p>
                <p className="text-sm mt-2 opacity-70">"{kind}" isn't registered.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Preview;
